import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import db from '../services/db';
import {
    IMAGE_EMBED_QUICK_PERCENTS,
    DEFAULT_IMAGE_EMBED_PERCENT,
    IMAGE_EMBED_MIN_PERCENT,
    IMAGE_EMBED_MAX_PERCENT,
    normalizeImageWidthPercent,
} from '../utils/imageEmbedSizes';

const ImageEmbedDialog = ({ isOpen, onClose, onImageSelected, folderPrefix = 'exercises/' }) => {
    const [mode, setMode] = useState('upload');
    const [isUploading, setIsUploading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const [pendingUrl, setPendingUrl] = useState(null);
    const [selectedPercent, setSelectedPercent] = useState(DEFAULT_IMAGE_EMBED_PERCENT);
    const fileInputRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setMode('upload');
            setSearchTerm('');
            setSearchResults([]);
            setDragActive(false);
            setPendingUrl(null);
            setSelectedPercent(DEFAULT_IMAGE_EMBED_PERCENT);
        }
    }, [isOpen]);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        if (mode === 'existing' && searchTerm.trim()) {
            searchTimeoutRef.current = setTimeout(() => {
                handleSearch(searchTerm);
            }, 300);
        } else if (mode === 'existing' && !searchTerm.trim()) {
            setSearchResults([]);
        }

        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, [searchTerm, mode]);

    const handleSearch = async (term) => {
        setIsSearching(true);
        try {
            const files = await db.listMediaFiles(folderPrefix, term);
            setSearchResults(files);
        } catch (error) {
            console.error('Error searching images:', error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const onSelectFile = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleUpload(e.target.files[0]);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleUpload(e.dataTransfer.files[0]);
        }
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleUpload = async (file) => {
        setIsUploading(true);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
            const filePath = `${folderPrefix}${fileName}`;

            const { error } = await supabase.storage.from('media').upload(filePath, file);
            if (error) throw error;

            const { data: { signedUrl } } = await supabase.storage
                .from('media')
                .createSignedUrl(filePath, 31536000);

            setPendingUrl(signedUrl);
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image: ' + error.message);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSelectExisting = async (file) => {
        try {
            const filePath = `${folderPrefix}${file.name}`;
            const { data: { signedUrl } } = await supabase.storage
                .from('media')
                .createSignedUrl(filePath, 31536000);
            setPendingUrl(signedUrl);
        } catch (error) {
            console.error('Error getting signed URL:', error);
            alert('Failed to load image: ' + error.message);
        }
    };

    const handleInsert = () => {
        if (!pendingUrl) return;
        onImageSelected(pendingUrl, { widthPercent: selectedPercent });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 2000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}>
            <div style={{
                background: 'white', borderRadius: '12px', maxWidth: '600px', width: '100%',
                maxHeight: '90vh', overflow: 'auto', position: 'relative', padding: '24px',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                        {pendingUrl ? 'Choose image size' : 'Embed Image'}
                    </h2>
                    <button
                        onClick={onClose}
                        type="button"
                        style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b', padding: '0 8px' }}
                    >×</button>
                </div>

                {pendingUrl ? (
                    <div>
                        <div style={{
                            background: '#f8fafc', borderRadius: '12px', border: '1.5px solid #e2e8f0',
                            padding: '1.25rem', marginBottom: '1.25rem', textAlign: 'center',
                        }}>
                            <img
                                src={pendingUrl}
                                alt="Preview"
                                style={{
                                    width: `${selectedPercent}%`,
                                    maxWidth: '100%',
                                    height: 'auto',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                    display: 'block',
                                    margin: '0 auto',
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '1.25rem' }}>
                            <div style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                                marginBottom: '0.5rem',
                            }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Width
                                </div>
                                <div style={{ fontSize: '1rem', fontWeight: 800, color: '#4361ee' }}>{selectedPercent}%</div>
                            </div>

                            <input
                                type="range"
                                min={IMAGE_EMBED_MIN_PERCENT}
                                max={IMAGE_EMBED_MAX_PERCENT}
                                value={selectedPercent}
                                onChange={(e) => setSelectedPercent(normalizeImageWidthPercent(e.target.value))}
                                style={{ width: '100%', marginBottom: '0.75rem', accentColor: '#4361ee' }}
                            />

                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '0.75rem' }}>
                                <input
                                    type="number"
                                    min={IMAGE_EMBED_MIN_PERCENT}
                                    max={IMAGE_EMBED_MAX_PERCENT}
                                    value={selectedPercent}
                                    onChange={(e) => setSelectedPercent(normalizeImageWidthPercent(e.target.value))}
                                    style={{
                                        width: '72px', padding: '8px 10px', borderRadius: '8px',
                                        border: '1.5px solid #e2e8f0', fontSize: '0.95rem', fontWeight: 700,
                                    }}
                                />
                                <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>% of content width</span>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {IMAGE_EMBED_QUICK_PERCENTS.map((pct) => {
                                    const active = selectedPercent === pct;
                                    return (
                                        <button
                                            key={pct}
                                            type="button"
                                            onClick={() => setSelectedPercent(pct)}
                                            style={{
                                                padding: '6px 12px', borderRadius: '999px',
                                                border: active ? '2px solid #4361ee' : '1.5px solid #e2e8f0',
                                                background: active ? '#eef2ff' : 'white',
                                                cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', color: '#1e293b',
                                            }}
                                        >{pct}%</button>
                                    );
                                })}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={() => setPendingUrl(null)}
                                style={{
                                    padding: '10px 18px', borderRadius: '10px', border: '1.5px solid #e2e8f0',
                                    background: 'white', color: '#64748b', fontWeight: 700, cursor: 'pointer',
                                }}
                            >Back</button>
                            <button
                                type="button"
                                onClick={handleInsert}
                                style={{
                                    padding: '10px 18px', borderRadius: '10px', border: 'none',
                                    background: '#4361ee', color: 'white', fontWeight: 700, cursor: 'pointer',
                                }}
                            >Insert image</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #e2e8f0' }}>
                            <button
                                onClick={() => setMode('upload')}
                                type="button"
                                style={{
                                    padding: '10px 16px', border: 'none',
                                    background: mode === 'upload' ? '#4361ee' : 'transparent',
                                    color: mode === 'upload' ? 'white' : '#64748b',
                                    fontWeight: 600, fontSize: '14px', cursor: 'pointer',
                                    borderBottom: mode === 'upload' ? '3px solid #4361ee' : '3px solid transparent',
                                    marginBottom: '-2px',
                                }}
                            >📷 New Upload</button>
                            <button
                                onClick={() => setMode('existing')}
                                type="button"
                                style={{
                                    padding: '10px 16px', border: 'none',
                                    background: mode === 'existing' ? '#4361ee' : 'transparent',
                                    color: mode === 'existing' ? 'white' : '#64748b',
                                    fontWeight: 600, fontSize: '14px', cursor: 'pointer',
                                    borderBottom: mode === 'existing' ? '3px solid #4361ee' : '3px solid transparent',
                                    marginBottom: '-2px',
                                }}
                            >🔍 Use Existing</button>
                        </div>

                        {mode === 'upload' ? (
                            <div
                                onDragOver={onDragOver}
                                onDragLeave={onDragLeave}
                                onDrop={onDrop}
                                onClick={() => fileInputRef.current?.click()}
                                style={{
                                    border: `2px dashed ${dragActive ? '#4361ee' : '#cbd5e1'}`,
                                    borderRadius: '12px', padding: '48px 24px', textAlign: 'center',
                                    cursor: 'pointer', background: dragActive ? '#f0f9ff' : '#f8fafc',
                                }}
                            >
                                <div style={{ fontSize: '48px', marginBottom: '16px' }}>{isUploading ? '⏳' : '📷'}</div>
                                <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                                    {isUploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={onSelectFile}
                                    style={{ display: 'none' }}
                                    disabled={isUploading}
                                />
                            </div>
                        ) : (
                            <div>
                                <input
                                    type="text"
                                    placeholder="Search by filename..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0',
                                        borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                                        marginBottom: '16px',
                                    }}
                                />
                                {isSearching ? (
                                    <div style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>Searching...</div>
                                ) : searchTerm.trim() === '' ? (
                                    <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8', fontSize: '14px' }}>
                                        Type a filename to search
                                    </div>
                                ) : searchResults.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8', fontSize: '14px' }}>
                                        No images found
                                    </div>
                                ) : (
                                    <div style={{
                                        maxHeight: '300px', overflowY: 'auto',
                                        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px',
                                    }}>
                                        {searchResults.map((file) => (
                                            <ImagePreviewItem
                                                key={file.name}
                                                file={file}
                                                folderPrefix={folderPrefix}
                                                onSelect={() => handleSelectExisting(file)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const ImagePreviewItem = ({ file, folderPrefix, onSelect }) => {
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadPreview = async () => {
            setLoading(true);
            try {
                const filePath = `${folderPrefix}${file.name}`;
                const { data: { signedUrl } } = await supabase.storage
                    .from('media')
                    .createSignedUrl(filePath, 31536000);
                setPreviewUrl(signedUrl);
            } catch (error) {
                console.error('Error loading preview:', error);
            } finally {
                setLoading(false);
            }
        };
        loadPreview();
    }, [file.name, folderPrefix]);

    return (
        <div
            onClick={onSelect}
            style={{
                aspectRatio: '1', borderRadius: '8px', overflow: 'hidden',
                border: '2px solid #e2e8f0', cursor: 'pointer', background: '#f8fafc', position: 'relative',
            }}
            title={file.name}
        >
            {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '24px' }}>⏳</div>
            ) : previewUrl ? (
                <img src={previewUrl} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '12px', color: '#94a3b8', padding: '8px', textAlign: 'center' }}>
                    Preview unavailable
                </div>
            )}
            <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '11px', padding: '4px 6px',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
                {file.name}
            </div>
        </div>
    );
};

export default ImageEmbedDialog;
